/**
 * useCampaignLogs Hook
 *
 * Fetches execution logs for a campaign and groups them by contact.
 * Accepts either { userCampaignId } (user view) or { templateId } (admin view)
 * and routes to the correct fetch function.
 *
 * @param {object} params
 * @param {string} [params.userCampaignId] - Fetch logs for a single user campaign
 * @param {string} [params.templateId]     - Fetch logs across all user campaigns for a template
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchExecutionLogsByUserCampaign,
  fetchExecutionLogsByTemplate,
  transformQueueItemToCamelCase,
} from '../api/supabase/automations';

function buildContactSummaries(logs) {
  const sorted = [...logs].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const byContact = new Map();
  for (const log of sorted) {
    const cid = log.contactId;
    if (!byContact.has(cid)) byContact.set(cid, []);
    byContact.get(cid).push(log);
  }

  const result = new Map();

  for (const [cid, contactLogs] of byContact) {
    let runIndex = 0;
    let currentRun = [];

    for (const log of contactLogs) {
      const nodes = log.contextData?.nodes;
      const isRunStart = !nodes || Object.keys(nodes).length === 0;

      if (isRunStart && currentRun.length > 0) {
        const key = `${cid}__run_${runIndex}`;
        result.set(key, {
          contact: currentRun[0].contact,
          logs: currentRun,
          latestLog: currentRun[currentRun.length - 1],
          stepCount: currentRun.length,
          runIndex,
        });
        runIndex++;
        currentRun = [];
      }

      currentRun.push(log);
    }

    if (currentRun.length > 0) {
      const key = `${cid}__run_${runIndex}`;
      result.set(key, {
        contact: currentRun[0].contact,
        logs: currentRun,
        latestLog: currentRun[currentRun.length - 1],
        stepCount: currentRun.length,
        runIndex,
      });
    }
  }

  return result;
}

function buildStats(contactSummaries) {
  let total = 0;
  let inProgress = 0;
  let completed = 0;
  let failed = 0;

  for (const [, entry] of contactSummaries) {
    total++;
    const latest = entry.latestLog;
    if (!latest) continue;

    const hasFailed = entry.logs.some((l) => l.status === 'failed');
    const hasPending = entry.logs.some(
      (l) => l.status === 'pending' || l.status === 'processing'
    );

    if (hasFailed) {
      failed++;
    } else if (hasPending) {
      inProgress++;
    } else if (latest.status === 'completed') {
      completed++;
    } else {
      inProgress++;
    }
  }

  return { total, inProgress, completed, failed };
}

export function useCampaignLogs({ userCampaignId, templateId } = {}) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!userCampaignId && !templateId) {
      setLogs([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      let rawData;
      if (userCampaignId) {
        rawData = await fetchExecutionLogsByUserCampaign(userCampaignId);
      } else {
        rawData = await fetchExecutionLogsByTemplate(templateId);
      }
      setLogs((rawData || []).map(transformQueueItemToCamelCase));
    } catch (err) {
      console.error('[useCampaignLogs] load error:', err);
      setError(err.message || 'Failed to load execution logs');
    } finally {
      setLoading(false);
    }
  }, [userCampaignId, templateId]);

  useEffect(() => {
    load();
  }, [load]);

  const contactSummaries = useMemo(() => buildContactSummaries(logs), [logs]);
  const stats = useMemo(() => buildStats(contactSummaries), [contactSummaries]);

  return {
    logs,
    contactSummaries,
    stats,
    loading,
    error,
    reload: load,
  };
}
